import * as Comlink from 'comlink';
import type {
  DecryptAndHashResult,
  DecryptedPayload,
  DecryptedWithThumbnail,
  DecryptWorkerApi,
} from '../../workers/decryptWorker.js';

interface PooledWorker {
  worker: Worker;
  api: Comlink.Remote<DecryptWorkerApi>;
  inFlight: number;
}

// Mirrors EncryptWorkerPool: spreads cv.decryptPhoto across N workers so the
// main thread never blocks on AES-GCM-256 of multi-MB blobs. The pool is a
// module-scoped singleton because the viewer / grid / thumb-strip all need to
// share it — instantiating per-component would defeat the parallelism.
class DecryptWorkerPool {
  private readonly workers: PooledWorker[];

  constructor(poolSize: number) {
    this.workers = Array.from({ length: poolSize }, () => {
      const worker = new Worker(new URL('../../workers/decryptWorker.ts', import.meta.url), {
        type: 'module',
      });
      return {
        worker,
        api: Comlink.wrap<DecryptWorkerApi>(worker),
        inFlight: 0,
      };
    });
  }

  async decrypt(packedBytes: Uint8Array, albumKey: CryptoKey): Promise<DecryptedPayload> {
    const slot = this.pickLeastBusy();
    slot.inFlight++;
    try {
      return await slot.api.decrypt(packedBytes, albumKey);
    } finally {
      slot.inFlight--;
    }
  }

  async decryptWithThumbnail(
    packedBytes: Uint8Array,
    albumKey: CryptoKey,
    mimeType: string,
  ): Promise<DecryptedWithThumbnail> {
    const slot = this.pickLeastBusy();
    slot.inFlight++;
    try {
      return await slot.api.decryptWithThumbnail(packedBytes, albumKey, mimeType);
    } finally {
      slot.inFlight--;
    }
  }

  async decryptAndHash(
    packedBytes: Uint8Array,
    albumKey: CryptoKey,
    opts: { keepBytes?: boolean } = {},
  ): Promise<DecryptAndHashResult> {
    const slot = this.pickLeastBusy();
    slot.inFlight++;
    try {
      return await slot.api.decryptAndHash(packedBytes, albumKey, opts);
    } finally {
      slot.inFlight--;
    }
  }

  // Fan-out a batch of small text payloads (e.g. encryptedRating digit
  // strings) across the pool. We chunk the input across workers rather than
  // sending the whole batch to one worker so a 600-photo page parallelises
  // 4-way. The chunk size is just ceil(N/poolSize) — no need for clever
  // load-balancing on payloads this small.
  async decryptTextsBatch(packed: string[], albumKey: CryptoKey): Promise<Array<string | null>> {
    if (packed.length === 0) return [];
    const n = this.workers.length;
    const chunkSize = Math.ceil(packed.length / n);
    const out: Array<string | null> = new Array(packed.length);
    await Promise.all(
      this.workers.map(async (slot, idx) => {
        const start = idx * chunkSize;
        const end = Math.min(start + chunkSize, packed.length);
        if (start >= end) return;
        const slice = packed.slice(start, end);
        slot.inFlight++;
        try {
          const decoded = await slot.api.decryptTextsBatch(slice, albumKey);
          for (let i = 0; i < decoded.length; i++) {
            out[start + i] = decoded[i] ?? null;
          }
        } finally {
          slot.inFlight--;
        }
      }),
    );
    return out;
  }

  private pickLeastBusy(): PooledWorker {
    let best = this.workers[0]!;
    for (const slot of this.workers) {
      if (slot.inFlight < best.inFlight) best = slot;
    }
    return best;
  }
}

const POOL_SIZE = 4;
let singleton: DecryptWorkerPool | null = null;

export function getDecryptPool(): DecryptWorkerPool {
  if (!singleton) singleton = new DecryptWorkerPool(POOL_SIZE);
  return singleton;
}
